import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";
import Breadcrumbs from "@/components/Breadcrumbs";
import {
  ArrowRight, Clock, Globe, MapPin, SealCheck, ShieldWarning, Users,
} from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import GuideListenButton from "@/components/GuideListenButton";

const TYPE_COLORS = {
  earthquake: "#B45309", cyclone: "#7C3AED", flood: "#0284C7", wildfire: "#DC2626",
  volcano: "#EA580C", drought: "#A16207", tsunami: "#0E7490", storm: "#0369A1",
  heatwave: "#DC2626", "cold-wave": "#2563EB", landslide: "#78350F", other: "#4B5563",
};
const STATUS_STYLE = {
  DETECTED: "bg-[#FEF3C7] text-[#92400E]", CONFIRMED: "bg-[#DBEAFE] text-[#1E40AF]",
  ACTIVE: "bg-[#FEE2E2] text-[#B91C1C]", UPDATING: "bg-[#FEF3C7] text-[#92400E]",
  ENDED: "bg-black/5 text-[#4B5563]", ARCHIVED: "bg-black/5 text-[#4B5563]",
};
const TIER_LABELS = { 1: "Official", 2: "Scientific", 3: "Professional media", 4: "Community" };

/** Canonical event page — the permanent public record of a weather/disaster
 * event (NaturePulse archive treatment for a WeatherPulse live event). */
export default function EventPage() {
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [explainer, setExplainer] = useState(null);

  useEffect(() => {
    setData(null); setMissing(false); setExplainer(null);
    http.get(`/events/${eventId}`).then(({ data }) => setData(data)).catch(() => setMissing(true));
    // Plain-language AI explainer — generated once per event, then cached.
    http.get(`/events/${eventId}/explainer`).then(({ data }) => setExplainer(data.explainer)).catch(() => {});
  }, [eventId]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const e = data?.event;

  return (
    <main className="min-h-screen bg-white text-[#0C2D48] pt-32 md:pt-36 pb-24" data-testid="event-page">
      <div className="max-w-[900px] mx-auto px-5 md:px-8 -mb-4">
        <ListenTranslateBar text={`A tracked weather or disaster event from the Pulse Event Engine — with timeline, sources and press archive.${e ? ` ${e.title}. ${e.summary || ""}` : ""}${explainer ? ` ${[explainer.whats_happening, explainer.about_this_type, explainer.safety_tip].filter(Boolean).join(" ")}` : ""}`} testidPrefix="ltbar-event" />
      </div>
      {e && (
        <SeoMeta
          title={`${e.title} — ${e.event_id} | Pulse Event Engine`}
          description={(e.summary || `${e.event_type} event — status ${e.status}, tracked by the NaturePulse Global Event Engine.`).slice(0, 155)}
          canonical={`${origin}/events/${e.event_id}`}
          ogType="article"
          jsonLd={{
            "@context": "https://schema.org", "@type": "Event",
            name: e.title, description: e.summary || e.title, url: `${origin}/events/${e.event_id}`,
            eventStatus: "https://schema.org/EventScheduled",
            location: e.location?.display_name ? { "@type": "Place", name: e.location.display_name } : undefined,
            startDate: e.started_at,
          }}
        />
      )}
      <div className="max-w-[900px] mx-auto px-5 md:px-8">
        {missing ? (
          <div className="mt-14 text-center" data-testid="event-missing">
            <h1 className="font-serif-display text-3xl">Event not found</h1>
            <p className="mt-2 text-sm text-[#4B5563]">This event ID doesn't exist — or was retracted.</p>
            <Link to="/weather-pulse/live" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0284C7] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white">
              Live dashboard <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
        ) : !e ? (
          <div className="mt-16 text-center text-sm text-[#6B7280]">Loading…</div>
        ) : (
          <>
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Weather Pulse", to: "/weather-pulse" }, { label: "Live", to: "/weather-pulse/live" }, { label: e.event_id }]} />

            {/* Header */}
            <div className="mt-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full px-3 py-1 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-white"
                  style={{ backgroundColor: TYPE_COLORS[e.event_type] || "#4B5563" }} data-testid="event-type">
                  {e.event_type}
                </span>
                <span className={`rounded-full px-3 py-1 font-mono-tech text-[9px] tracking-[0.2em] uppercase ${STATUS_STYLE[e.status] || "bg-black/5"}`} data-testid="event-status">
                  {e.status}
                </span>
                <span className="font-mono-tech text-[10px] tracking-[0.14em] text-[#6B7280]" data-testid="event-id">{e.event_id}</span>
              </div>
              <h1 className="mt-3 font-serif-display text-3xl md:text-5xl leading-tight text-[#0C2D48]" data-testid="event-title">{e.title}</h1>
              {e.summary && <p className="mt-3 text-base text-[#374151] leading-relaxed">{e.summary}</p>}
            </div>

            {/* AI plain-language explainer (generated on first view, cached) */}
            {explainer && (
              <section className="mt-6 rounded-3xl border border-[#BAE6FD] bg-[#F0F9FF] p-6" data-testid="event-explainer">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#0369A1]">In plain language</div>
                  <GuideListenButton
                    sections={[
                      { title: "What's happening", text: explainer.whats_happening },
                      { title: `About ${explainer.type_name || "this event type"}`, text: explainer.about_this_type },
                      { title: "Safety tip", text: explainer.safety_tip },
                    ].filter((s) => s.text)}
                    testid="event-listen" color="#0369A1" />
                </div>
                <p className="mt-3 text-base text-[#0C2D48] leading-relaxed font-medium" data-testid="explainer-whats-happening">{explainer.whats_happening}</p>
                {explainer.about_this_type && (
                  <p className="mt-3 text-sm text-[#374151] leading-relaxed" data-testid="explainer-about">
                    <span className="font-semibold text-[#0369A1]">What is a {(explainer.type_name || "hazard").toLowerCase()}? </span>
                    {explainer.about_this_type}
                  </p>
                )}
                {explainer.safety_tip && (
                  <p className="mt-3 rounded-xl border border-[#B45309]/30 bg-[#FFFBEB] p-3 text-sm text-[#78350F] leading-relaxed" data-testid="explainer-safety">
                    {explainer.safety_tip}
                  </p>
                )}
              </section>
            )}

            {/* At a glance */}
            <div className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="event-glance">
              {[
                e.location?.display_name && { icon: MapPin, label: "Location", value: e.location.display_name },
                { icon: Clock, label: "Started", value: (e.started_at || "").slice(0, 16).replace("T", " ") || "—" },
                { icon: Clock, label: "Last updated", value: (e.updated_at || "").slice(0, 16).replace("T", " ") },
                { label: "Severity", value: `${"●".repeat(e.severity)}${"○".repeat(5 - e.severity)} (${e.severity}/5)` },
                { label: "Confidence", value: `${e.confidence}/100` },
                e.gdacs?.population && e.gdacs.population !== "0" && { icon: Users, label: "Population in zone", value: e.gdacs.population },
              ].filter(Boolean).map((g) => (
                <div key={g.label} className="rounded-xl border border-black/5 bg-white p-3.5 shadow-sm">
                  <div className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#6B7280]">{g.label}</div>
                  <div className="mt-0.5 text-sm font-medium">{g.value}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-2xl border border-[#B45309]/25 bg-[#FFFBEB] p-4 flex gap-3" data-testid="event-safety">
              <ShieldWarning size={18} weight="fill" className="shrink-0 mt-0.5 text-[#B45309]" />
              <p className="text-sm text-[#78350F]">
                This is a machine-assembled event record, not an official warning. For emergencies follow your
                national meteorological service and local authorities.
              </p>
            </div>

            {/* Timeline */}
            <section className="mt-8" data-testid="event-timeline">
              <h2 className="font-serif-display text-2xl">Timeline</h2>
              <div className="mt-3 space-y-2">
                {(data.timeline || []).map((u, i) => (
                  <div key={i} className="flex gap-3 rounded-xl border border-black/5 bg-white p-3.5 shadow-sm" data-testid={`event-update-${i}`}>
                    <div className="shrink-0 font-mono-tech text-[9px] tracking-[0.14em] uppercase text-[#6B7280] w-28">
                      {(u.ts || "").slice(5, 16).replace("T", " ")}
                    </div>
                    <div className="text-sm text-[#1F2937]">
                      {u.text}
                      {u.source_name && (
                        <span className="ml-1 text-[#6B7280]">
                          — {u.source_url ? <a href={u.source_url} target="_blank" rel="noreferrer" className="text-[#0284C7] underline">{u.source_name}</a> : u.source_name}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* News & Reports archive */}
            {e.news?.length > 0 && (
              <section className="mt-8" data-testid="event-news">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif-display text-2xl">News &amp; reports</h2>
                  <span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#6B7280]" data-testid="event-news-count">
                    {e.news.length} archived
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-[#6B7280]">
                  A directory of press coverage — headlines and sources are archived, articles stay with their publishers.
                </p>
                <div className="mt-3 space-y-2">
                  {[...e.news].reverse().map((n, i) => (
                    <a key={n.url || i} href={n.url} target="_blank" rel="noreferrer" data-testid={`event-news-${i}`}
                      className="block rounded-xl border border-black/5 bg-white p-3.5 shadow-sm hover:border-[#0284C7]/40 transition-colors">
                      <div className="text-sm font-medium text-[#0C2D48] leading-snug">{n.headline}</div>
                      <div className="mt-1 font-mono-tech text-[8px] tracking-[0.16em] uppercase text-[#6B7280]">
                        {n.publisher}{(n.published_at || "").length >= 10 ? ` · ${n.published_at.slice(0, 10)}` : ""} · press
                      </div>
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* Sources */}
            {e.sources?.length > 0 && (
              <section className="mt-8" data-testid="event-sources">
                <h2 className="font-serif-display text-2xl">Sources</h2>
                <div className="mt-3 space-y-2">
                  {e.sources.map((s, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl border border-black/5 bg-white p-3.5 shadow-sm">
                      <SealCheck size={15} weight="fill" className={s.tier === 1 ? "text-[#166534]" : "text-[#6B7280]"} />
                      {s.url ? <a href={s.url} target="_blank" rel="noreferrer" className="text-sm text-[#0284C7] underline">{s.name}</a> : <span className="text-sm">{s.name}</span>}
                      <span className="rounded-full bg-black/5 px-2 py-0.5 font-mono-tech text-[8px] tracking-[0.14em] uppercase text-[#4B5563]">
                        Tier {s.tier} · {TIER_LABELS[s.tier] || "Source"}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-[#6B7280]">
                  Disaster alerts sourced from GDACS (Global Disaster Alert and Coordination System) — European Union, CC BY 4.0.
                </p>
              </section>
            )}

            {/* Related */}
            {data.related_events?.length > 0 && (
              <section className="mt-8" data-testid="event-related">
                <h2 className="font-serif-display text-2xl">Related events</h2>
                <div className="mt-3 space-y-2">
                  {data.related_events.map((r) => (
                    <Link key={r.event_id} to={`/events/${r.event_id}`} data-testid={`event-related-${r.event_id}`}
                      className="flex items-center justify-between rounded-xl border border-black/5 bg-white p-3.5 shadow-sm hover:border-[#0284C7]/40">
                      <span className="text-sm font-medium">{r.title}</span>
                      <Globe size={14} className="text-[#0284C7]" />
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/weather-pulse/live" data-testid="event-live-link"
                className="inline-flex items-center gap-2 rounded-full bg-[#0284C7] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white">
                Live dashboard <ArrowRight size={11} weight="bold" />
              </Link>
              <Link to="/weather-pulse" data-testid="event-wp-link"
                className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-black/5">
                Weather Pulse
              </Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}

export { TYPE_COLORS };
